import { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Shield, Users, Fingerprint, AlertTriangle, LogOut, ZapOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Admin-only "Security Audit" page. Read-only. Silent to end users — nothing
 * about a user's experience changes when an admin loads this page. All data
 * comes from what the server already stores at register + login (no client-side
 * tracking library, no cookies, no eye-icon on the user's screen).
 */
export default function AdminSecurity() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("signins"); // signins | signups | clusters
  const [signins, setSignins] = useState([]);
  const [signups, setSignups] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [busyId, setBusyId] = useState(null);
  const [nukeBusy, setNukeBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "signins") {
        const { data } = await api.get("/admin/security/signins?limit=100");
        setSignins(data.items || []);
      } else if (tab === "signups") {
        const { data } = await api.get(`/admin/security/signups?days=${days}`);
        setSignups(data.items || []);
      } else {
        const { data } = await api.get(`/admin/security/ip-clusters?days=${days}&min_accounts=2`);
        setClusters(data.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, days]);

  const forceLogoutUser = async (u) => {
    if (u.id === me?.id) {
      toast.error("You can't force-logout yourself.");
      return;
    }
    if (!window.confirm(`Force logout ${u.email}? Their current session will be ended immediately and they'll have to sign in again.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/security/users/${u.id}/force-logout`);
      toast.success(`${u.email} has been signed out.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const forceLogoutAll = async () => {
    const prompt = "This will sign out EVERY user (except you) immediately. They'll have to log in again. Type LOGOUT ALL to confirm:";
    const confirmed = window.prompt(prompt, "");
    if (!confirmed || confirmed.trim().toUpperCase() !== "LOGOUT ALL") {
      toast.info("Cancelled — confirmation phrase didn't match.");
      return;
    }
    setNukeBusy(true);
    try {
      const { data } = await api.post("/admin/security/logout-all");
      toast.success(`Signed out ${data.modified} user${data.modified === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNukeBusy(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <AppLayout>
      <PageHero
        eyebrow="Admin"
        title="Security Audit"
        subtitle="Silent, read-only view of recent sign-ins, signups, and shared-IP clusters. Users have no idea this page exists."
        icon={<Shield className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <Button
            onClick={forceLogoutAll}
            disabled={nukeBusy}
            variant="outline"
            className="rounded-none h-10 px-4 uppercase tracking-widest text-[11px] font-mono-num font-semibold border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950"
            data-testid="admin-security-logout-all"
          >
            <ZapOff className="w-3.5 h-3.5 mr-1.5" />
            {nukeBusy ? "Signing out…" : "Force Logout All Users"}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { k: "signins", label: "Recent Sign-ins", Icon: Users },
          { k: "signups", label: "New Accounts", Icon: Fingerprint },
          { k: "clusters", label: "Shared-IP Clusters", Icon: AlertTriangle },
        ].map(({ k, label, Icon }) => (
          <Button
            key={k}
            onClick={() => setTab(k)}
            variant={tab === k ? "default" : "outline"}
            className={`rounded-none h-10 px-4 uppercase tracking-widest text-[11px] font-mono-num font-semibold ${tab === k ? "bg-[#0D9488] text-black hover:bg-[#0F766E]" : ""}`}
            data-testid={`admin-security-tab-${k}`}
          >
            <Icon className="w-3.5 h-3.5 mr-1.5" /> {label}
          </Button>
        ))}
        {(tab === "signups" || tab === "clusters") && (
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ml-auto h-10 border border-slate-200 bg-white text-sm px-3 rounded-none font-mono-num"
            data-testid="admin-security-days"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        )}
      </div>

      <Card className="border border-slate-200 rounded-none overflow-hidden">
        {loading && <div className="p-8 text-sm text-slate-500 font-mono-num">Loading…</div>}

        {!loading && tab === "signins" && (
          <div className="divide-y divide-slate-100" data-testid="admin-security-signins">
            {signins.length === 0 ? <div className="p-6 text-slate-500 text-sm">No sign-ins.</div> :
             signins.map((u) => (
              <div key={u.id} className="p-3 flex flex-wrap items-baseline gap-2 gap-y-1 hover:bg-slate-50">
                <span className="font-display font-bold text-slate-900 text-sm truncate max-w-[180px]">{u.email}</span>
                {u.role && u.role !== "member" && <span className="font-mono-num text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#0D9488] text-black">{u.role}</span>}
                <span className="font-mono-num text-[11px] text-slate-500 tabular-nums ml-auto">{fmt(u.last_login_at)}</span>
                {u.id !== me?.id && (
                  <Button
                    onClick={() => forceLogoutUser(u)}
                    disabled={busyId === u.id}
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 rounded-none border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 uppercase tracking-widest text-[10px] font-mono-num font-semibold ml-2"
                    data-testid={`force-logout-btn-${u.id}`}
                  >
                    <LogOut className="w-3 h-3 mr-1" />
                    {busyId === u.id ? "…" : "Force logout"}
                  </Button>
                )}
                <div className="w-full flex flex-wrap gap-x-4 gap-y-0.5 font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
                  <span>logins <b className="text-slate-800 normal-case">{u.login_count}</b></span>
                  <span>ip <b className="text-slate-800 normal-case">{u.last_login_ip || "—"}</b></span>
                  <span className="truncate max-w-[400px]">ua <b className="text-slate-800 normal-case">{u.last_login_user_agent || "—"}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "signups" && (
          <div className="divide-y divide-slate-100" data-testid="admin-security-signups">
            {signups.length === 0 ? <div className="p-6 text-slate-500 text-sm">No new accounts in this window.</div> :
             signups.map((u) => (
              <div key={u.id} className="p-3 flex flex-wrap items-baseline gap-2 gap-y-1 hover:bg-slate-50">
                <span className="font-display font-bold text-slate-900 text-sm truncate max-w-[220px]">{u.email}</span>
                <span className="font-mono-num text-[11px] text-slate-500 tabular-nums ml-auto">{fmt(u.created_at)}</span>
                <div className="w-full flex flex-wrap gap-x-4 gap-y-0.5 font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
                  <span>signup ip <b className="text-slate-800 normal-case">{u.signup_ip || "—"}</b></span>
                  <span>logins <b className="text-slate-800 normal-case">{u.login_count}</b></span>
                  {u.login_count === 0 && <span className="text-amber-600 font-semibold">never logged back in</span>}
                  <span className="truncate max-w-[400px]">ua <b className="text-slate-800 normal-case">{u.signup_user_agent || "—"}</b></span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "clusters" && (
          <div className="divide-y divide-slate-100" data-testid="admin-security-clusters">
            {clusters.length === 0 ? (
              <div className="p-6 text-slate-500 text-sm">
                No IP has produced 2+ signups in this window — the coast looks clear.
              </div>
            ) : clusters.map((c) => (
              <div key={c.ip} className="p-4">
                <div className="flex items-baseline gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30]" />
                  <span className="font-display font-bold text-slate-900 text-base tabular-nums">{c.ip}</span>
                  <span className="font-mono-num text-[10px] uppercase tracking-widest text-[#FF3B30] font-semibold">
                    · {c.count} accounts
                  </span>
                </div>
                <ol className="ml-5 space-y-1 border-l border-slate-200 pl-3">
                  {c.accounts.map((a, i) => (
                    <li key={i} className="text-xs font-mono-num tabular-nums text-slate-700 flex flex-wrap gap-x-3">
                      <span className="text-slate-900 font-semibold">{a.email || a.user_id}</span>
                      <span className="text-slate-500">{fmt(a.at)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AppLayout>
  );
}
